import styled from '@emotion/styled'
import {css, keyframes} from '@emotion/react'
import {theme} from '../../../theme'
import {Button} from '../../../shared/Button'
import {
  Panel,
  LocationCard,
  LocationCardRow,
  LocationCardMainIcon,
  LocationCardDetailsCol,
  LocationCardTopRow,
  LocationCardIndexLabel,
  LocationCardName,
  LocationCardPay,
} from './PaymentDetailsStep.styles'

export {
  Panel,
  PanelTitle,
  PageHeading,
  HeadingTitle,
  HeadingSubtitle,
  LocationCardRow,
  LocationCardMainIcon,
  LocationCardDetailsCol,
  LocationCardTopRow,
  LocationCardIndexLabel,
  LocationCardName,
  LocationCardPay,
} from './PaymentDetailsStep.styles'

// background/border match CatalogGrid.styles.js's Wrapper (theme.colors.bg.brandAlt
// + theme.colors.border.alt) so this step's outer panel reads the same as
// Catalog Selection's.
export const LocationSchedulePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[5]};
  // background: ${theme.colors.bg.brandAlt};
  // border: 1px solid ${theme.colors.border.alt};
  background-color: rgb(244, 246, 251);
  border: 1px solid rgb(229, 231, 235);
  border-radius: ${theme.radii.xl};
  padding: ${theme.space[2.5]};
`

// Two-column layout: the left-hand location tab list + whichever location's
// scheduler is currently active on the right. Collapses to stacked on
// narrow viewports since the tab list and the calendar/slot grid are both
// already designed to go full-width on mobile. align-items: stretch (the
// flex default) is required, not just cosmetic — LocationTabList's
// border-right divider needs the box itself to span the full row height,
// not just its own (usually shorter) content height; the tab cards inside
// it aren't affected since they're sized by their own gap-separated column,
// not stretched individually.
export const SchedulingLayout = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${theme.space[5]};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

// border-right is the divider between the location list and whichever
// location's scheduler is active on the right — switches to a border-bottom
// once SchedulingLayout stacks the two columns on narrow viewports. Color
// matches LocationDetailPanel/OnboardingContactWrapper's own border
// (theme.colors.border.default) instead of the lighter border.light this
// used to have — the mismatch made the divider look out of place next to
// the panels it separates.
export const LocationTabList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[3]};
  width: 280px;
  flex-shrink: 0;
  // padding-right: ${theme.space[5]};
  padding: 4px 20px 4px 10px;
  border-right: 1px solid ${theme.colors.border.default};

  @media (max-width: 768px) {
    width: 100%;
    padding-right: 0;
    padding-bottom: ${theme.space[5]};
    border-right: none;
    border-bottom: 1px solid ${theme.colors.border.default};
  }
`

// Fills the rest of SchedulingLayout's row next to the fixed-width
// LocationTabList and stacks one LocationDetailPanel per named call the
// active location needs (kickoff and/or Thryv Success Services). No
// background of its own — OnboardingContactWrapper and each
// LocationDetailPanel own their own white background separately instead of
// sharing one wrapping card.
export const LocationDetailStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[4]};
  flex: 1;
  min-width: 0;
`

// Panel, widened to fill LocationDetailStack (bare Panel has no inline-size
// opinion, so without this it'd shrink to fit its content instead of the
// remaining space). box-shadow: none overrides Panel's own shadow — this
// step's call sections read flatter against LocationSchedulePanel's
// background than Panel's other usages (e.g. PaymentDetailsStep).
export const LocationDetailPanel = styled(Panel)`
  flex: 1;
  min-width: 0;
  box-shadow: none;
`

// The named-call heading at the top of each LocationDetailPanel — "Thryv
// Kickoff Call" / "Thryv Success Services Call", same names the legacy
// onboarding-program screen shows above each call section. justify-content:
// space-between pins an optional trailing control (SkipCallButton) to the
// end of this same row instead of stacking it below — the title's own text
// is wrapped in a <span> by callers so it and the trailing control are two
// separate flex children.
export const CallSectionTitle = styled.h3`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space[2]};
  // margin: 0 0 ${theme.space[4]};
  padding: 0 0 ${theme.space[1]};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
`

// Reuses PaymentDetailsStep's LocationCard shell (icon box + rows, 10px
// radius, no border, same box-shadow/padding/background) so both steps'
// location cards read as the identical style, on top of which this adds
// only what LocationCard doesn't need — it's clickable/selectable, so a
// pointer cursor and a focus ring for the active card.
export const LocationTabCard = styled(LocationCard)`
  text-align: left;
  cursor: pointer;
  transition: box-shadow ${theme.transitions.fast};

  ${({active}) =>
    active &&
    css`
      box-shadow: 0 0 0 1px ${theme.colors.border.focus},
        0px 2px 6.1px 0px #0000002b;
    `}

  &:hover {
    box-shadow: 0 0 0 1px ${theme.colors.border.focus}80,
      0px 2px 6.1px 0px #0000002b;
  }
`

export const LocationTabRow = LocationCardRow
export const LocationTabMainIcon = LocationCardMainIcon
export const LocationTabDetailsCol = LocationCardDetailsCol
export const LocationTabTopRow = LocationCardTopRow
export const LocationTabIndexLabel = LocationCardIndexLabel
export const LocationTabName = LocationCardName
export const LocationTabMeta = LocationCardPay

export const LocationTabTimezone = styled.span`
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.regular};
  color: ${theme.colors.text.primary};
`

// `skipped` (every required call resolved, but via Skip rather than a real
// booking — see LocationTab's allSkipped) renders a neutral gray instead of
// `done`'s green, so a skipped location never reads as an actual booking at
// a glance. Takes priority over `done` when both are true.
export const LocationTabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px ${theme.space[2]};
  border-radius: ${theme.radii.full};
  font-size: ${theme.fontSizes.xxs};
  font-weight: ${theme.fontWeights.semibold};
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({done, skipped}) =>
    skipped
      ? theme.colors.text.secondary
      : done
      ? theme.colors.status.success.text
      : theme.colors.status.warning.text};
  background: ${({done, skipped}) =>
    skipped
      ? theme.colors.bg.secondary
      : done
      ? theme.colors.status.success.bg
      : theme.colors.status.warning.bg};
  border: 1px solid
    ${({done, skipped}) =>
      skipped
        ? theme.colors.border.default
        : done
        ? theme.colors.status.success.border
        : theme.colors.status.warning.border};
`

// Spanish toggle + Region select, shown above the calendar/slot columns for
// the Kickoff call only (see AccountSetup/utils/schedulingLanguageRules.js).
// Styled to match ProposalTool's ClientProposalSchedule (SpanishRow/
// ToggleGroup/ToggleBtn in pages/ProposalTool/features/ClientProposalSchedule/
// styled.components.js) so the same control reads identically in both
// scheduling flows. #E1EAFF/#0e30ac/#374151 are copied verbatim from that
// component — #E1EAFF matches theme.colors.bg.infoLight (palette.info[25])
// exactly, but the navy accent and label gray have no equivalent design
// token, so they're hardcoded here for pixel parity rather than approximated.
// Row layout so the Spanish toggle and Region select sit side by side —
// align-items: flex-end keeps their bottom edges level (the Select renders
// its own label above the field, same as LanguageRegionRow's label above the
// toggle). Wraps to stacked on narrow viewports.
export const LanguageRegionSection = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: ${theme.space[5]};
  margin-bottom: ${theme.space[4]};
`

// align-items: flex-start keeps this row's children (label, toggle track) at
// their own natural width — without it, column-direction flex's default
// stretch made LanguageToggle's background track fill this row's full width
// (sized by the long label above it) instead of hugging the Yes/No buttons.
export const LanguageRegionRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.space[2]};
  flex-shrink: 0;
`

// nowrap so the question always renders on one line — this label sits
// beside RegionSelectWrap's own "Select Region" label (both inside
// LanguageRegionSection), and a wrapped second line here would knock the two
// labels out of alignment.
export const LanguageRegionLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.bold};
  color: #374151;
  white-space: nowrap;
`

// height: 32px matches the Region select's own height (Select size="sm" —
// see Select.styles.js's sizeStyles) so the two controls line up when they
// sit side by side in LanguageRegionSection.
export const LanguageToggle = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  background: ${theme.colors.bg.infoLight};
  border-radius: ${theme.radii.lg};
  padding: 2px;
  gap: 3px;
  flex-shrink: 0;
`

export const LanguageOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: ${({active}) =>
    active ? theme.colors.bg.primary : 'transparent'};
  padding: 0 18px;
  border-radius: ${theme.radii.md};
  border: 1px solid ${({active}) => (active ? '#0e30ac' : 'transparent')};
  color: ${({active}) => (active ? '#0e30ac' : '#374151')};
  font-size: ${theme.fontSizes.md};
  font-weight: ${({active}) =>
    active ? theme.fontWeights.semibold : theme.fontWeights.regular};
  cursor: pointer;
  white-space: nowrap;
  transition: background ${theme.transitions.fast},
    color ${theme.transitions.fast};
`

// Same column layout + label-to-control gap as LanguageRegionRow, so its
// own LanguageRegionLabel ("Select Region") sits above the field the same
// way the Spanish question sits above its toggle.
export const RegionSelectWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.space[2]};
  width: 240px;
  flex-shrink: 0;
`

export const ScheduleColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.space[5]};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

export const ColumnLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.space[2]};
`

// Wraps each of ScheduleColumns' two children (the calendar / the slot
// list) so both stretch to the same height — ScheduleColumns is a grid row,
// which by default stretches every item in it to the row's height (i.e. the
// taller one, the calendar) — height: 100% here is what lets SlotGrid
// below actually fill that stretched space instead of just sizing to its
// own content.
export const ScheduleColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
`

// flex: 1 + min-height: 0 (flex items don't shrink past content size by
// default) is what lets this actually scroll within ScheduleColumn's
// stretched height instead of growing taller than the calendar.
export const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  grid-auto-rows: min-content;
  gap: ${theme.space[2]};
  align-content: flex-start;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

export const SlotBtn = styled.button`
  padding: ${theme.space[2]} ${theme.space[1]};
  border-radius: ${theme.radii.md};
  border: 1px solid
    ${({selected}) =>
      selected ? theme.colors.border.focus : theme.colors.border.default};
  background: ${({selected}) =>
    selected ? theme.colors.bg.blueSubtleBg : theme.colors.bg.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${({selected}) =>
    selected ? theme.fontWeights.semibold : theme.fontWeights.regular};
  cursor: pointer;
`

export const SlotsEmpty = styled.p`
  grid-column: 1 / -1;
  margin: ${theme.space[4]} 0;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  text-align: center;
`

const spin = keyframes`to { transform: rotate(360deg); }`

// Shown in place of the slot grid while its fetch is in flight — scoped to
// just this section (not the calendar) so picking a date never blocks the
// calendar itself.
export const SlotsLoading = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.space[2]};
  padding: ${theme.space[6]} 0;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
`

export const SlotsSpinner = styled.span`
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid ${theme.colors.border.default};
  border-top-color: ${theme.colors.action.primary};
  border-radius: ${theme.radii.full};
  animation: ${spin} 0.8s linear infinite;
`

// The booked-call confirmation strip — light blue panel, calendar icon,
// "Thank you, your <call> is scheduled for <date>." and a Reset link on the
// right, matching the legacy onboarding-program confirmation.
export const ScheduledNotice = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[3]};
  padding: ${theme.space[3]} ${theme.space[4]};
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.bg.blueSubtleBg};
  border: 1px solid ${theme.colors.border.focus}55;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  > svg {
    flex-shrink: 0;
  }
`

export const ScheduledNoticeText = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  line-height: 1.4;

  u {
    font-weight: ${theme.fontWeights.bold};
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  b {
    font-weight: ${theme.fontWeights.bold};
  }
`

// Link-styled Reset control on the confirmation strip: reset icon + label,
// brand-blue, no button chrome.
export const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.space[2]};
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: ${theme.colors.border.focus};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

// Pill-shaped "Schedule Call" button (Button's own base radius is only
// theme.radii.md), uppercase label — matches the reference design.
export const ScheduleCallButton = styled(Button)`
  border-radius: ${theme.radii.full};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: ${theme.fontWeights.bold};
`

export const ScheduleActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.space[2]};
  margin-top: ${theme.space[4]};
`

export const ErrorText = styled.p`
  margin: ${theme.space[2]} 0 0;
  color: ${theme.colors.status.danger.text};
  font-size: ${theme.fontSizes.sm};
`

/* ── react-calendar overrides — adapted from ProposalTool's
   ClientProposalSchedule, retheme's only, same DOM/class contract ── */
export const CalendarWrap = styled.div`
  .react-calendar {
    width: 100%;
    border: 1px solid ${theme.colors.border.default};
    border-radius: ${theme.radii.lg};
    font-family: ${theme.fonts.body};
    font-size: ${theme.fontSizes.md};
    color: ${theme.colors.text.primary};
    overflow: hidden;
  }

  .react-calendar__navigation {
    display: flex;
    align-items: center;
    padding: ${theme.space[2.5]} ${theme.space[2]} ${theme.space[2]};
    border-bottom: 1px solid ${theme.colors.border.default};
    margin-bottom: 0;
  }

  .react-calendar__navigation button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: ${theme.fontSizes.xl};
    color: ${theme.colors.text.primary};
    padding: ${theme.space[1]} ${theme.space[2]};
    border-radius: ${theme.radii.sm};
    min-width: 28px;

    &:hover:not(:disabled) {
      background: ${theme.colors.bg.secondary};
    }

    &:disabled {
      color: ${theme.colors.text.muted};
      cursor: not-allowed;
    }
  }

  .react-calendar__navigation__label {
    font-weight: ${theme.fontWeights.semibold};
    font-size: ${theme.fontSizes.base};
    color: ${theme.colors.text.primary};
    flex-grow: 1;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.space[1]};
  }

  .react-calendar__month-view__weekdays {
    text-align: center;
    padding: ${theme.space[1.5]} 0 ${theme.space[0.5]};
  }

  .react-calendar__month-view__weekdays__weekday {
    color: ${theme.colors.text.secondary};
    font-size: ${theme.fontSizes.xs};
    font-weight: ${theme.fontWeights.medium};
    padding: ${theme.space[1]} 0;
  }

  .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
  }

  .react-calendar__tile {
    background: none;
    border: none;
    padding: ${theme.space[1]} 0;
    cursor: pointer;
    font-family: ${theme.fonts.body};
    font-size: ${theme.fontSizes.md};
    color: ${theme.colors.text.primary};

    abbr {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: ${theme.radii.full};
      margin: 0 auto;
      transition: background ${theme.transitions.fast};
    }

    &:hover:not(:disabled) abbr {
      background: ${theme.colors.bg.secondary};
    }

    &:disabled {
      color: ${theme.colors.border.strong};
      cursor: not-allowed;
    }
  }

  .react-calendar__tile--now abbr {
    background: ${theme.colors.status.warning.icon};
    color: ${theme.colors.text.inverse};
  }

  .react-calendar__tile--active abbr,
  .react-calendar__tile--active:hover abbr {
    background: ${theme.colors.action.primary} !important;
    color: ${theme.colors.text.inverse} !important;
  }

  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus,
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: transparent;
  }

  .react-calendar__month-view__days__day--neighboringMonth {
    color: ${theme.colors.text.muted};
  }
`
